<template>
    <MPModal
        v-model="open"
        variant="center"
        size="3xl"
        :closeButton="true"
        :persistent="duplicateConfirmationOpen || deleteConfirmationOpen"
    >
        <template #header>
            <div class="text-muval-gray-1 flex min-w-0 flex-col">
                <h3 class="text-lg font-medium">{{ isEdit ? 'Edit diary entry' : 'New diary entry' }}</h3>
                <div
                    v-if="isEdit"
                    class="text-muval-gray-2 flex min-w-0 items-center gap-2 text-sm"
                >
                    <span
                        class="truncate"
                        v-text="currentEntry?.title"
                    ></span>
                    <span
                        v-if="currentEntry?.type?.name"
                        class="bg-brand/10 text-brand flex h-5 shrink-0 items-center rounded-full px-2 text-xs font-medium"
                        v-text="currentEntry.type.name"
                    ></span>
                    <!--
                        The status lives here as a changer rather than in the
                        footer as a pair of "Mark complete" / "Cancel entry"
                        buttons. Those read as destructive actions sitting beside
                        Save, and offered no way back from either; a status is a
                        field on the record, so it belongs beside the record's
                        identity and behaves like every other status in the CRM.
                    -->
                    <CompanyDiaryStatusSelect
                        class="shrink-0"
                        :modelValue="currentEntry?.status ?? 'TO_DO'"
                        :disabled="!canModify || statusSaving"
                        @update:modelValue="setStatus"
                    />
                </div>
            </div>
        </template>

        <!--
            The tab bar lives in `sub-header` because that slot survives the modal's
            own body scroll: on a long note thread or file list the tabs have to
            stay reachable without scrolling back to the top.
        -->
        <template #sub-header>
            <!--
                `mb-[-13px]` is the sub-header's 12px padding plus one more, so the
                tab bar's own bottom border lands exactly on the sub-header's rather
                than 1px above it. They are the same colour but different widths -
                the bar's is inset by this wrapper's padding - so a 1px offset reads
                as one stepped, doubled rule instead of a single divider.
            -->
            <ScrollGradientHorizontal class="-mx-4 -mt-3 mb-[-13px] sm:-mx-6">
                <div class="min-w-max px-4 sm:px-6">
                    <MuvalTabs
                        :tabs="tabs"
                        :activeTabIndex="activeTabIndex"
                        barOnly
                        indicatorAutoWidth
                        @change="activeTabIndex = $event"
                    />
                </div>
            </ScrollGradientHorizontal>
        </template>

        <div class="text-muval-gray-1 min-h-96">
            <div
                v-show="activeTab === 'general'"
                class="flex flex-col gap-5"
            >
                <div class="form-row gap-5">
                    <div class="form-titled-item grow">
                        <span class="font-medium">Type</span>
                        <MuvalSelect
                            v-model="form.company_diary_entry_type_id"
                            vid="company_diary_entry_type_id"
                            :selectOptions="entryTypeOptions"
                            valueProp="id"
                            displayLabel="name"
                            placeholder="Select a type"
                            :canSearch="true"
                            :disabled="!canModify"
                        />
                    </div>
                    <div
                        v-if="!canViewOwnEntriesOnly"
                        class="form-titled-item grow"
                    >
                        <span class="font-medium">Depot</span>
                        <MuvalSelect
                            v-model="form.depot_id"
                            :selectOptions="depotOptions ?? []"
                            valueProp="id"
                            displayLabel="name"
                            placeholder="No depot"
                            :canSearch="true"
                            :disabled="!canModify"
                        />
                    </div>
                </div>

                <div class="form-titled-item">
                    <span class="font-medium">Title</span>
                    <MuvalInput
                        v-model="form.title"
                        vid="title"
                        placeholder="e.g., Annual service - Truck 12"
                        :disabled="!canModify"
                    />
                </div>

                <div class="form-titled-item">
                    <span class="font-medium">Description</span>
                    <MuvalTextArea
                        v-model="form.description"
                        placeholder="Optional detail"
                        :disabled="!canModify"
                    />
                </div>

                <MuvalBanner
                    v-if="form.staff_ids.length"
                    type="info"
                >
                    <template v-if="hydrationEntry && hydrationEntry.staff_dates_are_local === false">
                        Existing availability times will be kept unless you change the dates. New dates apply in each
                        staff member’s local timezone.
                    </template>
                    <template v-else>Dates and times apply in each staff member’s local timezone.</template>
                </MuvalBanner>

                <div class="flex items-center justify-between gap-4">
                    <span class="text-sm font-medium">All day</span>
                    <MuvalSwitch
                        v-model="form.all_day"
                        :disabled="!canModify"
                    />
                </div>

                <!--
                    Two controls rather than one that changes shape, because the
                    values they hold are different: an all-day entry is authored as
                    whole days and the API expands them to the 00:00:00 → 23:59:59
                    sentinel, while a timed one carries the times the operator
                    picked. Swapping between them clears the other's value rather
                    than trying to carry a time across a mode that has none.
                -->
                <div
                    v-if="form.all_day"
                    class="form-titled-item"
                >
                    <span class="font-medium">Dates</span>
                    <MPDatetime
                        v-model:startDate="form.start_datetime"
                        v-model:endDate="form.end_datetime"
                        startVid="start_datetime"
                        endVid="end_datetime"
                        isRange
                        hasIcon
                    />
                </div>
                <div
                    v-else
                    class="form-titled-item"
                >
                    <span class="font-medium">Dates and times</span>
                    <MPDatetime
                        v-model:startDate="form.start_datetime"
                        v-model:endDate="form.end_datetime"
                        startVid="start_datetime"
                        endVid="end_datetime"
                        isRange
                        hasTime
                        hasIcon
                    />
                </div>

                <p
                    v-if="selectedTypeBlocks"
                    class="rounded-muval-2 bg-muval-gray-6 text-muval-gray-2 px-3 py-2 text-xs"
                >
                    This type blocks availability - everyone and everything assigned to it is held unavailable for these
                    dates.
                </p>

                <!--
                    Reminders are only offered once the entry has a scheduled event
                    to hang them off: the API refuses one on an unscheduled entry.
                -->
                <div
                    v-if="isEdit"
                    class="flex flex-col gap-3"
                >
                    <hr />
                    <CompanyDiaryReminderList
                        :entry="currentEntry"
                        :canModify="canModify"
                    />
                </div>
            </div>

            <div v-show="activeTab === 'addresses'">
                <CompanyDiaryAddressPanel
                    v-model="form.addresses"
                    :hasWindow="hasWindow"
                    :suggestions="selectedTypeAddresses"
                    :suggestionsLoading="entryTypesRefreshing"
                    :suggestionsError="entryTypesError"
                    @retry="refreshEntryTypes"
                    :disabled="!canModify"
                />
            </div>

            <!--
                Four resource tabs over three payload fields: containers and
                trailers share the API's single `vessel_ids`. The split is a CRM
                concern - {@see splitVessels} and {@see payload} are the only two
                places that know about it - because the backend has one vessel pivot
                and adding a discriminator to the payload would encode a UI decision
                in the schema.
            -->
            <div
                v-for="kind in RESOURCE_KINDS"
                v-show="activeTab === kind"
                :key="kind"
            >
                <CompanyDiaryResourcePanel
                    v-model="form[`${kind}_ids`]"
                    :kind="kind"
                    :startDatetime="form.start_datetime"
                    :endDatetime="form.end_datetime"
                    :allDay="form.all_day"
                    :excludeEntryId="currentEntry?.id ?? null"
                    :assigned="assignedFor(kind)"
                    :disabled="!canModify"
                />
            </div>

            <div v-show="activeTab === 'notes'">
                <CompanyDiaryNotesPanel
                    :entryId="threadEntryId"
                    :canModify="canModify"
                />
            </div>

            <div v-show="activeTab === 'files'">
                <CompanyDiaryFilesPanel
                    :entryId="currentEntry?.id ?? null"
                    :canModify="canModify"
                />
            </div>
        </div>

        <template #footer>
            <div class="form-row text-muval-gray-1 w-full flex-col-reverse items-center justify-between lg:flex-row">
                <div class="flex items-center gap-2">
                    <MuvalButton
                        v-if="isEdit && canModify"
                        type="button"
                        quinary
                        :loading="duplicating"
                        @click="duplicate"
                    >
                        Duplicate
                    </MuvalButton>
                    <MuvalButton
                        v-if="canDelete"
                        type="button"
                        quinary
                        class="border-muval-error! text-muval-error!"
                        @click="deleteConfirmationOpen = true"
                    >
                        Delete
                    </MuvalButton>
                </div>
                <div class="form-row flex-col-reverse justify-end lg:flex-row">
                    <MuvalButton
                        type="button"
                        quaternary
                        @click="open = false"
                    >
                        {{ isDirty ? 'Discard changes' : 'Close' }}
                    </MuvalButton>
                    <MuvalButton
                        v-if="canModify && !isEdit"
                        type="button"
                        tertiary
                        :loading="savingAction === 'stay-open'"
                        :disabled="savingAction !== null"
                        @click="save('stay-open')"
                    >
                        Save
                    </MuvalButton>
                    <MuvalButton
                        v-if="canModify"
                        type="button"
                        :loading="savingAction === 'close'"
                        :disabled="savingAction !== null"
                        @click="save('close')"
                    >
                        {{ isEdit ? 'Save' : 'Save and close' }}
                    </MuvalButton>
                </div>
            </div>
        </template>
    </MPModal>

    <MPModal
        v-if="duplicateConfirmationOpen"
        v-model="duplicateConfirmationOpen"
        variant="center"
        size="sm"
        title="Discard changes and duplicate?"
        persistent
    >
        <p class="text-muval-gray-2 text-sm">
            This will discard your unsaved changes and start a duplicate from the last saved version of this entry.
        </p>

        <template #footer>
            <div class="form-row flex-col-reverse justify-end lg:flex-row">
                <MuvalButton
                    type="button"
                    quaternary
                    @click="duplicateConfirmationOpen = false"
                >
                    Cancel
                </MuvalButton>
                <MuvalButton
                    type="button"
                    @click="confirmDuplicate"
                >
                    Discard changes and duplicate
                </MuvalButton>
            </div>
        </template>
    </MPModal>

    <MPModal
        v-if="deleteConfirmationOpen"
        v-model="deleteConfirmationOpen"
        variant="center"
        size="sm"
        title="Delete diary entry?"
        persistent
    >
        <p class="text-muval-gray-2 text-sm">
            Delete <span class="text-muval-gray-1 font-medium">{{ currentEntry?.title }}</span
            >? This permanently removes the diary entry and cannot be undone.
        </p>

        <template #footer>
            <div class="form-row flex-col-reverse justify-end lg:flex-row">
                <MuvalButton
                    type="button"
                    quaternary
                    :disabled="deleting"
                    @click="deleteConfirmationOpen = false"
                >
                    Keep entry
                </MuvalButton>
                <MuvalButton
                    type="button"
                    :loading="deleting"
                    @click="deleteEntry"
                >
                    Delete entry
                </MuvalButton>
            </div>
        </template>
    </MPModal>
</template>

<script setup lang="ts">
import companyDiaryApi, {
    type CompanyDiaryEntry,
    type CompanyDiaryEntryPayload,
    type CompanyDiaryEntryStatus,
    type CompanyDiaryResourceKindKey,
    type CompanyDiaryResourceSummary,
} from '@/api/companyDiary';
import { useCompanyDiaryEntryTypes } from '@composables/useCompanyDiaryOptions';
import { useCompanyDiaryPermissions } from '@composables/useCompanyDiaryPermissions';
import { useDepotFilterOptions } from '@composables/useResourceFilterOptions';
import MuvalTabs from '@components/MuvalTabs.vue';
import ScrollGradientHorizontal from '@components/ScrollGradientHorizontal.vue';
import CompanyDiaryAddressPanel from './CompanyDiaryAddressPanel.vue';
import CompanyDiaryFilesPanel from './CompanyDiaryFilesPanel.vue';
import CompanyDiaryNotesPanel from './CompanyDiaryNotesPanel.vue';
import CompanyDiaryResourcePanel from './CompanyDiaryResourcePanel.vue';
import CompanyDiaryStatusSelect from './CompanyDiaryStatusSelect.vue';
import { COMPANY_DIARY_STATUS_TOASTS } from './companyDiaryStatusOptions';
import { RESOURCE_KINDS, TAB_KEYS, TAB_LABELS, splitVessels, tabIndexOf, type TabKey } from './companyDiaryEntryTabs';
import { toast } from 'vue-sonner';

/**
 * Create and edit a company diary entry (spec company-diary-entries), as a tabbed
 * record editor rather than one long form.
 *
 * **What each tab persists.** General, Addresses and the four resource tabs are
 * one `PATCH`, written by Save. Notes and Files write themselves as they are used
 * - they are not part of the entry's payload - which is why they are unavailable
 * until the entry exists to hang them off.
 *
 * **Create offers both outcomes; Save closes an edit.** A create can stay open so
 * Notes, Files and reminders become available without reopening the entry, or save
 * and close when the operator is finished. Saving an existing entry closes as it
 * did before. Close says "Discard changes" while there are any.
 *
 * The dates round-trip through the API's `all_day` flag rather than through
 * hand-composed times: the entry's event stores the whole-day sentinel
 * `00:00:00 → 23:59:59`, and composing that here would be a second implementation
 * of a convention that already has exactly one owner on the server.
 *
 * Resourcing happens here rather than after the fact, unlike the operations diary
 * where an event is created first and resourced from its own modals. A staff
 * unavailability *is* the staff member, and a supply run is chosen by which truck
 * is free - separating the two would make the commonest entries a two-step job.
 * What the operations diary contributes instead is the *shape* of the picker:
 * availability at the point of choice, not a dropdown of every name.
 */

const props = defineProps<{
    modelValue: boolean;
    /** The entry being edited, or null when creating. */
    entry?: CompanyDiaryEntry | null;
    canModify: boolean;
    /** Pre-selected window, used when the calendar is clicked on an empty day. */
    initialStart?: string | null;
    initialEnd?: string | null;
    initialStaff?: CompanyDiaryResourceSummary[];
    initialTransports?: CompanyDiaryResourceSummary[];
    initialContainers?: CompanyDiaryResourceSummary[];
    initialTrailers?: CompanyDiaryResourceSummary[];
    initialTypeName?: string;
    initialTypeKey?: string;
    /**
     * The tab to open on. The list index deep-links here - its Staff cell opens
     * the Staff tab, its Addresses cell the Addresses tab - so the operator lands
     * on the thing they clicked rather than on General.
     */
    initialTab?: TabKey | null;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'saved', entry: CompanyDiaryEntry): void;
    (e: 'deleted'): void;
}>();

const open = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit('update:modelValue', value),
});

/**
 * `modalEntry` lets a newly-created draft become an edit immediately even on
 * surfaces that only refresh their surrounding data after `saved`. During a duplicate
 * `duplicateSource` holds the fresh persisted snapshot used to hydrate the form, while
 * `currentEntry` deliberately becomes null so every ordinary create-only rule
 * (buttons, payload route, notes/files locking and conflict checks) applies.
 */
const modalEntry = ref<CompanyDiaryEntry | null>(props.entry ?? null);
const duplicateSource = ref<CompanyDiaryEntry | null>(null);
const currentEntry = computed(() => (duplicateSource.value ? null : modalEntry.value));
const hydrationEntry = computed(() => duplicateSource.value ?? currentEntry.value);
const isEdit = computed(() => Boolean(currentEntry.value?.id));
const canDelete = computed(() => isEdit.value && props.canModify && !currentEntry.value?.sourceable_type);

const { entryTypes, entryTypesRefreshing, entryTypesError, refreshEntryTypes } = useCompanyDiaryEntryTypes({
    includeInactive: true,
});

const selectedTypeAddresses = computed(() =>
    entryTypesRefreshing.value || entryTypesError.value ?
        []
    :   (entryTypes.value.find((type) => type.id === form.value.company_diary_entry_type_id)?.saved_addresses ?? []),
);
// Crew open entries read-only and the depots endpoint refuses them, so they get no depot field.
const { canViewOwnEntriesOnly } = useCompanyDiaryPermissions();
const { depotOptions } = useDepotFilterOptions({ enabled: () => !canViewOwnEntriesOnly.value });

/**
 * The picker offers active types, plus - when editing - the entry's own type even
 * if it has since been retired. Dropping it would silently re-point the entry at
 * whatever landed first in the list on the next save.
 */
const entryTypeOptions = computed(() => {
    const options = entryTypes.value.filter((type) => type.active);
    const current = hydrationEntry.value?.type;

    if (current?.id && !options.some((type) => type.id === current.id)) {
        options.unshift({
            id: current.id,
            name: `${current.name} (inactive)`,
            colour: current.colour ?? null,
            icon: current.icon ?? null,
            is_system: false,
            system_key: null,
            locked: false,
            active: false,
            blocks_availability: current.blocks_availability,
        });
    }

    return options;
});

const selectedTypeBlocks = computed(
    () =>
        entryTypes.value.find((type) => type.id === form.value.company_diary_entry_type_id)?.blocks_availability ??
        hydrationEntry.value?.type?.blocks_availability ??
        false,
);

const initialTypeId = computed(
    () =>
        entryTypes.value.find(
            (type) =>
                type.active &&
                (props.initialTypeKey ? type.system_key === props.initialTypeKey
                : props.initialTypeName ? type.name.toLowerCase() === props.initialTypeName.toLowerCase()
                : false),
        )?.id ?? null,
);

const emptyForm = () => ({
    company_diary_entry_type_id: initialTypeId.value,
    title: '',
    description: '' as string | null,
    /**
     * A window handed in by the calendar is whole days - the grid has no times to
     * offer - so the modal opens in all-day mode. Opening it timed would show the
     * clicked day at 12:00 AM, a time the operator never picked.
     */
    all_day: Boolean(props.initialStart),
    start_datetime: props.initialStart ?? null,
    end_datetime: props.initialEnd ?? null,
    depot_id: null as number | null,
    addresses: [] as string[],
    staff_ids: (props.initialStaff ?? []).map((staff) => staff.id),
    transport_ids: (props.initialTransports ?? []).map((resource) => resource.id),
    container_ids: (props.initialContainers ?? []).map((resource) => resource.id),
    trailer_ids: (props.initialTrailers ?? []).map((resource) => resource.id),
});

const assignedFor = (kind: CompanyDiaryResourceKindKey): CompanyDiaryResourceSummary[] => {
    const entry = hydrationEntry.value;

    if (!entry)
        return (
            {
                staff: props.initialStaff,
                transport: props.initialTransports,
                container: props.initialContainers,
                trailer: props.initialTrailers,
            }[kind] ?? []
        );

    if (kind === 'staff') return entry.staff ?? [];
    if (kind === 'transport') return entry.transports ?? [];

    const split = splitVessels(entry.vessels ?? []);

    return kind === 'trailer' ? split.trailers : split.containers;
};

const form = ref(emptyForm());
watch(initialTypeId, () => {
    if (props.modelValue && !isEdit.value && !form.value.company_diary_entry_type_id && initialTypeId.value) {
        const wasPristine = JSON.stringify(form.value) === pristine.value;
        form.value.company_diary_entry_type_id = initialTypeId.value;
        if (wasPristine) pristine.value = JSON.stringify(form.value);
    }
});
type SaveAction = 'stay-open' | 'close';

const savingAction = ref<SaveAction | null>(null);
const duplicating = ref(false);
const duplicateConfirmationOpen = ref(false);
const deleteConfirmationOpen = ref(false);
const deleting = ref(false);
let duplicateRequest = 0;
const statusSaving = ref(false);
const activeTabIndex = ref(0);
const fieldValidation = useForm();

/** What the form looked like when it was last hydrated or saved. */
const pristine = ref(JSON.stringify(emptyForm()));
const isDirty = computed(() => props.canModify && JSON.stringify(form.value) !== pristine.value);

const hasWindow = computed(() => Boolean(form.value.start_datetime && form.value.end_datetime));

/** The thread hangs off the entry's schedule service, so an unscheduled entry has none. */
const threadEntryId = computed(() => (currentEntry.value?.is_scheduled ? currentEntry.value.id : null));

/**
 * A count of zero is sent as `undefined` rather than `0`: `MuvalTabs` renders any
 * integer as a badge, and a row of grey zeroes across six tabs reads as clutter
 * where the badge is meant to be a signal that something is there.
 */
const badgeCount = (value: number): number | undefined => (value > 0 ? value : undefined);

const tabs = computed(() =>
    TAB_KEYS.map((key) => {
        const locked = (key === 'notes' || key === 'files') && !isEdit.value;

        return {
            key,
            name: TAB_LABELS[key],
            count:
                key === 'addresses' ? badgeCount(form.value.addresses.filter((address) => address.trim() !== '').length)
                : RESOURCE_KINDS.includes(key as CompanyDiaryResourceKindKey) ?
                    badgeCount(form.value[`${key as CompanyDiaryResourceKindKey}_ids`].length)
                :   undefined,
            disabled: locked,
            tooltip:
                !locked ? TAB_LABELS[key]
                : key === 'notes' ? 'Create the entry to start its note thread.'
                : 'Create the entry before attaching files to it.',
        };
    }),
);

const activeTab = computed<TabKey>(() => TAB_KEYS[activeTabIndex.value] ?? 'general');

const trimForMode = (value: string | null, allDay: boolean): string | null => {
    if (!value) return null;

    return allDay ? value.slice(0, 10) : value.slice(0, 16).replace('T', ' ');
};

const hydrate = (entry: CompanyDiaryEntry | null = currentEntry.value) => {
    fieldValidation.setErrors({});

    if (!entry) {
        form.value = emptyForm();
    } else {
        form.value = {
            company_diary_entry_type_id: entry.company_diary_entry_type_id,
            title: entry.title,
            description: entry.description,
            all_day: entry.all_day,
            /**
             * The stored window is the whole-day sentinel when `all_day` is set, and
             * the date control in that mode speaks days - so the times are trimmed
             * on the way in and re-expanded by the API on the way out.
             */
            start_datetime: trimForMode(entry.start_datetime, entry.all_day),
            end_datetime: trimForMode(entry.end_datetime, entry.all_day),
            depot_id: entry.depot_id,
            addresses: (entry.addresses ?? []).map((address) => address.label ?? '').filter(Boolean),
            staff_ids: entry.staff.map((resource) => resource.id),
            transport_ids: entry.transports.map((resource) => resource.id),
            container_ids: splitVessels(entry.vessels).containers.map((resource) => resource.id),
            trailer_ids: splitVessels(entry.vessels).trailers.map((resource) => resource.id),
        };
    }

    pristine.value = JSON.stringify(form.value);
};

watch(
    () => [props.modelValue, props.entry?.id, props.entry?.updated_at],
    ([isOpen]) => {
        if (!isOpen) {
            duplicateConfirmationOpen.value = false;
            deleteConfirmationOpen.value = false;
            duplicateSource.value = null;
            modalEntry.value = props.entry ?? null;
            ++duplicateRequest;
            duplicating.value = false;
            deleting.value = false;

            return;
        }

        // A parent refresh must not replace an unsaved duplicate draft in progress.
        if (duplicateSource.value) return;

        modalEntry.value = props.entry ?? null;
        hydrate(modalEntry.value);
    },
    { immediate: true },
);

/**
 * A closed modal reopens on the tab the caller asked for, and on General when it
 * asked for nothing - never on wherever it was last left.
 */
watch(
    () => props.modelValue,
    (isOpen) => {
        activeTabIndex.value = isOpen ? tabIndexOf(props.initialTab) : 0;
        if (isOpen) void refreshEntryTypes();
    },
    { immediate: true },
);

/**
 * Switching mode clears the window rather than converting it. A time carried into
 * all-day mode is meaningless, and a date carried out of it would silently become
 * midnight-to-midnight - a range the API rejects for ending at its own start.
 */
watch(
    () => form.value.all_day,
    (allDay, previous) => {
        if (allDay === previous) return;

        form.value.start_datetime = trimForMode(form.value.start_datetime, allDay);
        form.value.end_datetime = trimForMode(form.value.end_datetime, allDay);
    },
);

const payload = (): CompanyDiaryEntryPayload => ({
    company_diary_entry_type_id: form.value.company_diary_entry_type_id ?? undefined,
    title: form.value.title.trim(),
    description: form.value.description || null,
    all_day: form.value.all_day,
    start_datetime: form.value.start_datetime,
    end_datetime: form.value.end_datetime,
    depot_id: form.value.depot_id,
    addresses: form.value.addresses.map((address) => address.trim()).filter(Boolean),
    staff_ids: form.value.staff_ids,
    transport_ids: form.value.transport_ids,
    /** The two vessel tabs rejoin here - the API has one pivot for both. */
    vessel_ids: [...form.value.container_ids, ...form.value.trailer_ids],
});

const firstError = (err: any, fallback: string): string => {
    const errors = err?.errors;
    const firstKey = errors && Object.keys(errors)[0];

    return (firstKey && errors[firstKey]?.[0]) || err?.message || fallback;
};

const normaliseValidationErrors = (errors: Record<string, string | string[]>): Record<string, string> =>
    Object.fromEntries(
        Object.entries(errors).map(([field, message]) => [field, Array.isArray(message) ? message[0] : message]),
    );

/**
 * The modal owns a plain draft rather than vee-validate values because six tabs
 * contribute to one API payload. Registering the visible kit controls with this
 * local form context still gives them the CRM's standard red border and inline
 * message without introducing a second source of truth for the draft.
 */
const validateRequiredFields = (): boolean => {
    const errors: Record<string, string> = {};

    if (!form.value.company_diary_entry_type_id) errors.company_diary_entry_type_id = 'Choose an entry type.';
    else if (
        !isEdit.value &&
        entryTypeOptions.value.find((type) => type.id === form.value.company_diary_entry_type_id)?.active === false
    ) {
        errors.company_diary_entry_type_id = 'Choose an active entry type.';
    }
    if (!form.value.title.trim()) errors.title = 'Enter a title.';

    if (!form.value.start_datetime && !form.value.end_datetime) {
        errors.start_datetime = 'Choose a date range.';
        errors.end_datetime = 'Choose a date range.';
    } else {
        if (!form.value.start_datetime) errors.start_datetime = 'Choose a start date.';
        if (!form.value.end_datetime) errors.end_datetime = 'Choose an end date.';
    }

    fieldValidation.setErrors(errors);

    if (Object.keys(errors).length === 0) return true;

    activeTabIndex.value = 0;
    toast.error('Check the highlighted fields.');

    return false;
};

const save = async (action: SaveAction) => {
    fieldValidation.setErrors({});
    if (!validateRequiredFields()) return;

    savingAction.value = action;
    try {
        const { data } =
            isEdit.value ?
                await companyDiaryApi.updateEntry(currentEntry.value!.id, payload())
            :   await companyDiaryApi.createEntry(payload());

        const wasEdit = isEdit.value;

        toast.success(wasEdit ? 'Diary entry updated.' : 'Diary entry created.');
        pristine.value = JSON.stringify(form.value);

        if (!wasEdit) {
            duplicateSource.value = null;
            modalEntry.value = data.entry;
        }

        emit('saved', data.entry);

        /*
         * Edit retains its single save-and-close path. Create makes the choice
         * explicit: stay for Notes/Files/reminders, or finish and dismiss.
         */
        if (wasEdit || action === 'close') {
            open.value = false;
        }
    } catch (err: any) {
        if (err?.errors) {
            fieldValidation.setErrors(normaliseValidationErrors(err.errors));
            activeTabIndex.value = 0;
        }
        toast.error(firstError(err, 'Failed to save the diary entry.'));
    } finally {
        savingAction.value = null;
    }
};

/**
 * A status change is its own write, applied the moment it is picked rather than
 * waiting for Save - the same as the sales index and the order services list, and
 * the same as the footer buttons this replaced.
 *
 * The modal stays open - unlike Save, which closes an edit. Picking a status is
 * not the operator saying they are finished.
 */
const setStatus = async (status: CompanyDiaryEntryStatus) => {
    const entry = currentEntry.value;
    if (!entry || status === entry.status) return;

    statusSaving.value = true;
    try {
        const { data } = await companyDiaryApi.updateEntry(entry.id, { status });
        modalEntry.value = data.entry;
        toast.success(COMPANY_DIARY_STATUS_TOASTS[status] ?? 'Diary entry updated.');
        emit('saved', data.entry);
    } catch (err: any) {
        toast.error(firstError(err, 'Failed to update the diary entry.'));
    } finally {
        statusSaving.value = false;
    }
};

/**
 * Read immediately before hydrating so Duplicate always starts from what is durable,
 * never from edits in this form or from a stale row/list response. Until this
 * request succeeds the current edit is left entirely untouched.
 */
const beginDuplicate = async () => {
    const sourceEntry = currentEntry.value;
    if (!sourceEntry) return;

    const sourceId = sourceEntry.id;
    const request = ++duplicateRequest;
    duplicating.value = true;

    try {
        const { data } = await companyDiaryApi.showEntry(sourceId);

        if (request !== duplicateRequest || !props.modelValue || currentEntry.value?.id !== sourceId) return;

        duplicateSource.value = data.entry;
        activeTabIndex.value = 0;
        hydrate(data.entry);
    } catch (err: any) {
        if (request === duplicateRequest) toast.error(firstError(err, 'Failed to prepare the diary entry duplicate.'));
    } finally {
        if (request === duplicateRequest) duplicating.value = false;
    }
};

const duplicate = () => {
    if (!currentEntry.value) return;

    if (isDirty.value) {
        duplicateConfirmationOpen.value = true;

        return;
    }

    void beginDuplicate();
};

const confirmDuplicate = () => {
    duplicateConfirmationOpen.value = false;
    void beginDuplicate();
};

const deleteEntry = async () => {
    const entry = currentEntry.value;
    if (!entry || !canDelete.value) return;

    deleting.value = true;
    try {
        await companyDiaryApi.destroyEntry(entry.id);
        toast.success('Diary entry deleted.');
        deleteConfirmationOpen.value = false;
        open.value = false;
        emit('deleted');
    } catch (err: any) {
        toast.error(firstError(err, 'Failed to delete the diary entry.'));
    } finally {
        deleting.value = false;
    }
};
</script>

<style scoped>
/*
 * The tab strip scrolls on narrow viewports, but its scrollbar must not be
 * painted: the sub-header reserves an exact height, and a permanent macOS track
 * both eats 11px of it and drives a wedge between the active tab's underline and
 * the divider the underline is meant to sit on.
 */
.scrollbar-hide,
.scrollbar-hide :deep(*) {
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    scrollbar-width: none; /* Firefox */
}

/*
 * Descendants included: MuvalTabs carries its own `max-md:overflow-x-scroll`, so
 * below 768px the track would be painted by an element this component does not
 * own and a rule scoped to the wrapper alone would miss it.
 */
.scrollbar-hide::-webkit-scrollbar,
.scrollbar-hide :deep(*::-webkit-scrollbar) {
    display: none; /* Safari and Chrome */
}
</style>
